import { Badge, Box, IconButton, Tooltip } from "@mui/material";
import ScoutIcon from "components/ScoutIcon";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenantId } from "hooks/useTenant";
import { useChatSurfaceCoordinator } from "providers/ChatSurfaceCoordinator";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { brandColors } from "../../../../util";
import { useScoutConversation } from "../hooks/useScoutConversation";
import { hasScoutAccess } from "../scoutAccess";
import { PopoverScout } from "./PopoverScout";
import { useScoutPopoverSession } from "./ScoutPopoverSessionContext";
import { useScoutMinimizedCompletionBadge } from "./useScoutMinimizedCompletionBadge";

// `/o/:tenantId/scout` and `/o/:tenantId/scout/:conversationId` — the full-page
// Scout view. The popover would be redundant on top of the page it mirrors.
const FULL_SCOUT_PAGE_REGEX = /^\/o\/[^/]+\/scout(\/|$)/;

// Matches NAV_ACTION_SIZE in TopNavBarV2 so the launcher sits flush with the
// Avatar and NotificationBell buttons.
const LAUNCHER_SIZE = 40;

// Design tokens from Figma: the launcher is transparent when the popover is
// dismissed and swaps to the light peach surface while the popover is open.
// The peach isn't in our brand ramp yet — hard-coded to the Figma spec.
const OPEN_BG = "#FFEFEA";
const ICON_COLOR = brandColors["encamp-orange"][500];
const DISABLED_ICON_COLOR = brandColors["encamp-gray"][400];
const OPEN_HOVER_BG = brandColors["encamp-orange"][100];
const CLOSED_HOVER_BG = brandColors["encamp-orange"][50];

// Stable hook for AppCues to anchor tours/flows against, independent of any
// styling or copy changes to the button.
const APPCUES_ANCHOR_ID = "scout-launcher";

// Popover position constraints per design:
//   - hangs from just below the launcher, down to 30px above the viewport bottom
//   - right edge sits 8px past the page-content right edge (when whitespace is
//     available in fixed-width mode), and never closer than 24px to the viewport
//     right edge
const POPOVER_WIDTH_PX = 480;
const POPOVER_BOTTOM_GAP_PX = 30;
const POPOVER_TOP_GAP_PX = 4;
const POPOVER_PAGE_EDGE_GAP_PX = 8;
const POPOVER_VIEWPORT_EDGE_GAP_PX = 24;

type PopoverLayout = {
  anchorPosition: { top: number; left: number };
  height: string;
};

function computePopoverLayout(buttonRect: DOMRect | undefined): PopoverLayout {
  const viewportWidth = typeof window === "undefined" ? 0 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? 0 : window.innerHeight;
  const pageEl =
    typeof document === "undefined"
      ? null
      : document.querySelector<HTMLElement>('[data-testid="main-v2"]');
  const pageRight = pageEl?.getBoundingClientRect().right ?? viewportWidth;

  const top = (buttonRect?.bottom ?? 0) + POPOVER_TOP_GAP_PX;
  // When the viewport extends past the page's right edge, the popover's right
  // edge sits 8px past the page edge (poking into the whitespace). Clamp so
  // it never sits closer than 24px to the viewport right edge — this becomes
  // the operative rule when the page fills (or nearly fills) the viewport.
  const desiredRight = pageRight + POPOVER_PAGE_EDGE_GAP_PX;
  const maxRight = viewportWidth - POPOVER_VIEWPORT_EDGE_GAP_PX;
  const right = Math.min(desiredRight, maxRight);
  const height = `${Math.max(0, viewportHeight - top - POPOVER_BOTTOM_GAP_PX)}px`;

  return {
    anchorPosition: { top, left: right },
    height,
  };
}

function getLauncherTooltip(input: {
  isStreaming: boolean;
  showCompletionBadge: boolean;
  isMinimized: boolean;
}): string {
  if (input.isStreaming) {
    return "Scout is thinking…";
  }
  if (input.showCompletionBadge) {
    return "Scout has a new response";
  }
  if (input.isMinimized) {
    return "Resume Scout";
  }
  return "Ask Scout";
}

function getLauncherAriaLabel(input: {
  isStreaming: boolean;
  showCompletionBadge: boolean;
  isMinimized: boolean;
}): string {
  if (input.isStreaming) {
    return "Scout is thinking";
  }
  if (input.showCompletionBadge) {
    return "Scout has a new response";
  }
  if (input.isMinimized) {
    return "Resume Scout";
  }
  return "Ask Scout";
}

/**
 * Scout launcher rendered inline in the top navigation bar between the user
 * profile info and the account avatar. Opens a Popover Scout conversation
 * anchored to the button; supports a minimized state that keeps the session
 * alive and surfaces spin/completion affordances on the same button.
 */
export function ScoutTopNavTrigger() {
  const { featureFlags } = useFeatureFlags();
  const { hasPermissions } = useAuthorization();
  const { isStaff, isEmulating } = useCurrentUser();
  const scoutEnabled = hasScoutAccess({
    flags: featureFlags,
    hasPermissions,
    isStaff: isStaff && !isEmulating,
  });

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [popoverLayout, setPopoverLayout] = useState<PopoverLayout | null>(null);
  const session = useScoutPopoverSession();
  const { viewMode } = session;
  const chatSurfaceCoordinator = useChatSurfaceCoordinator();
  const tenantId = useTenantId();
  const { pathname } = useLocation();
  const isOpen = viewMode === "open";
  const isMinimized = viewMode === "minimized";

  // Recompute anchor + height whenever the popover is opened and on resize
  // while it's visible. Skipped when closed so we don't pay for layout reads
  // on every window resize when Scout isn't in use.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const recompute = () =>
      setPopoverLayout(computePopoverLayout(buttonRef.current?.getBoundingClientRect()));
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [isOpen]);

  const handleDismissScoutSurface = useCallback(() => {
    // Hide the popover but keep the conversation (e.g. handoff to Front chat).
    session.close();
  }, [session]);

  useEffect(() => {
    if (!chatSurfaceCoordinator) {
      return undefined;
    }
    return chatSurfaceCoordinator.registerDismissScout(handleDismissScoutSurface);
  }, [chatSurfaceCoordinator, handleDismissScoutSurface]);

  const { activeTurn, isSending } = useScoutConversation({
    tenantId,
    conversationId: session.conversationId,
    onConversationIdChange: session.setConversationId,
    // The launcher only reads turn status for the spin/completion affordances.
    // The popover's own instance owns private-document fetching.
    enablePrivateDocuments: false,
  });
  const { showCompletionBadge, clearCompletionBadge } = useScoutMinimizedCompletionBadge(
    isMinimized,
    activeTurn?.status
  );
  const isStreaming =
    isSending || activeTurn?.status === "starting" || activeTurn?.status === "streaming";

  const handleOpen = useCallback(() => {
    chatSurfaceCoordinator?.notifyScoutPopoverOpening();
    clearCompletionBadge();
    session.open();
  }, [chatSurfaceCoordinator, clearCompletionBadge, session]);
  const handleLauncherClick = useCallback(() => {
    // Toggle behavior: clicking the launcher while the popover is open should
    // minimize it (keeping the session), matching how chat-widget launchers
    // typically behave.
    if (viewMode === "open") {
      session.minimize();
      return;
    }
    handleOpen();
  }, [handleOpen, session, viewMode]);
  const handleClose = useCallback(() => {
    clearCompletionBadge();
    // X is an explicit "I'm done with this chat" — start fresh next time.
    // Minimize keeps the session; only this path resets it.
    session.reset();
  }, [clearCompletionBadge, session]);
  const handleMinimize = useCallback(() => session.minimize(), [session]);

  // Hide the launcher entirely when Scout isn't available for this user or
  // when there's no usable tenant (Scout's API requires one — the popover
  // would open but `sendMessage` would throw).
  if (!scoutEnabled || !tenantId) {
    return null;
  }

  // Disable the launcher when the user is already on the full Scout page.
  const isDisabled = FULL_SCOUT_PAGE_REGEX.test(pathname);
  const iconColor = isDisabled ? DISABLED_ICON_COLOR : ICON_COLOR;

  const showBadge = isMinimized && showCompletionBadge;
  const showSpin = isMinimized && isStreaming;
  const iconAnimation = showSpin ? "scoutSpin 2s linear infinite" : "none";
  const tooltip = getLauncherTooltip({
    isStreaming: showSpin,
    showCompletionBadge: showBadge,
    isMinimized,
  });
  const ariaLabel = getLauncherAriaLabel({
    isStreaming: showSpin,
    showCompletionBadge: showBadge,
    isMinimized,
  });

  return (
    <>
      <Tooltip
        title={isDisabled ? "Scout is already open" : tooltip}
        enterDelay={100}
        disableInteractive
      >
        <Box>
          <IconButton
            ref={buttonRef}
            id={APPCUES_ANCHOR_ID}
            data-appcues-id={APPCUES_ANCHOR_ID}
            onClick={handleLauncherClick}
            aria-label={ariaLabel}
            disabled={isDisabled}
            sx={{
              width: LAUNCHER_SIZE,
              height: LAUNCHER_SIZE,
              borderRadius: "50%",
              bgcolor: isOpen ? OPEN_BG : "transparent",
              color: iconColor,
              transition: "background-color 150ms ease, transform 150ms ease",
              "&:hover": {
                bgcolor: isOpen ? OPEN_HOVER_BG : CLOSED_HOVER_BG,
              },
              "&:active": {
                transform: "scale(0.96)",
              },
            }}
          >
            <Badge
              color="warning"
              variant="dot"
              overlap="circular"
              invisible={!showBadge}
              sx={{
                "& .MuiBadge-badge": {
                  boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
                },
              }}
            >
              <ScoutIcon
                sx={{
                  fontSize: 24,
                  color: iconColor,
                  animation: iconAnimation,
                  transformOrigin: "center",
                  "@keyframes scoutSpin": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              />
            </Badge>
          </IconButton>
        </Box>
      </Tooltip>
      <PopoverScout
        anchorEl={buttonRef.current}
        // Never paint the popover on top of the full Scout page — a rehydrated
        // "open" view mode is intentionally suppressed there (the page shows a
        // "resume conversation" chip instead).
        open={isOpen && !isDisabled}
        onClose={handleClose}
        closeScout={handleDismissScoutSurface}
        onMinimize={handleMinimize}
        session={session}
        width={`${POPOVER_WIDTH_PX}px`}
        {...(popoverLayout
          ? { anchorPosition: popoverLayout.anchorPosition, height: popoverLayout.height }
          : {})}
      />
    </>
  );
}
